import { AiOutlineSearch } from "react-icons/ai";
import React, { useMemo } from "react";

interface Props {
  name: string;
  value: string;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  type?:
    | "email"
    | "text"
    | "password"
    | "number"
    | "date"
    | "tel"
    | "search"
    | "select";
  placeholder?: string;
  required?: boolean;
  label?: string;
  id?: string;
  className?: string;
  inputClassName?: string;
  options?: string[];
  readOnly?: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  handleSearch?: () => void;
  isSearchButton?: boolean;
  min?: string;
  max?: string;
}

export default function CustomInput({
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  required = false,
  label,
  id,
  inputClassName,
  className,
  options = [],
  readOnly,
  disabled,
  onBlur,
  handleSearch,
  isSearchButton,
  min,
  max,
}: Props) {
  // Dynamically include the current value if not in options and not empty
  const updatedOptions = useMemo(() => {
    if (
      type === "select" &&
      value.trim().length > 0 &&
      !options.includes(value)
    ) {
      return [...options, value];
    }
    return options;
  }, [options, value, type]);

  return (
    <div
      className={
        "flex flex-col gap-2 w-full relative " +
        className +
        (disabled ? " opacity-50" : "")
      }>
      {label && (
        <label
          htmlFor={id || name}
          className="text-[13px] font-medium text-slate-700">
          {label}
        </label>
      )}

      {type === "select" ? (
        <select
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled || readOnly}
          className={
            "h-11 px-3.5 border border-slate-300 rounded-xl outline-none hover:border-slate-400 focus:ring-4 focus:ring-brand-main/15 focus:border-brand-main bg-white text-sm text-slate-800 transition disabled:bg-slate-50 " +
            inputClassName
          }>
          <option value="">Select {label}</option>

          {updatedOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id || name}
          name={name}
          type={type}
          value={value}
          onBlur={onBlur}
          onChange={(e) =>
            onChange({
              ...e,
              target: {
                ...e.target,
                name: e.target.name,
                value: e.target.value?.toLocaleUpperCase(),
              },
            })
          }
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          min={min}
          max={max}
          className={
            "h-11 px-3.5 border border-slate-300 rounded-xl outline-none hover:border-slate-400 focus:ring-4 focus:ring-brand-main/15 focus:border-brand-main bg-white text-sm text-slate-800 placeholder-slate-400 transition read-only:bg-slate-50 disabled:bg-slate-50 " +
            inputClassName
          }
        />
      )}

      {isSearchButton && (
        <AiOutlineSearch
          onClick={handleSearch}
          size={32}
          className="absolute right-1.5 bottom-1.5 text-brand-main hover:bg-brand-main/10 p-1.5 rounded-lg cursor-pointer transition-all ease-in-out"
        />
      )}
    </div>
  );
}
